import { DatePipe } from '@angular/common';
import { effect, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { VariableImportItem, VariableImportResponse } from '@selfai-platform/pipeline-common';
import { AlertService, downloadJson } from '@selfai-platform/shared';
import { DialogService } from '@selfai-platform/shell';
import { VariablesImportResultComponent } from '../components/variables-import-result/variables-import-result.component';
import { VariablesUploadFormComponent } from '../components/variables-upload-form/variables-upload-form.component';
import { WorkflowVariablesStore } from '../store';

@Injectable()
export class VariablesExportImportService {
  private readonly variablesStore = inject(WorkflowVariablesStore);
  private readonly datePipe = inject(DatePipe);
  private readonly alertService = inject(AlertService);
  private readonly uploadDialogService = inject(DialogService<VariableImportItem[]>);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  exportVariables(workflowId: string): void {
    try {
      const variables = this.variablesStore.getVariablesForExport();

      const currentDate = new Date();
      const fileName = `variables_${workflowId}_${this.datePipe.transform(currentDate, 'yyyyMMdd_HHmm')}.json`;

      downloadJson(variables, fileName);
    } catch (error) {
      this.alertService.error(String(error));
    }
  }

  importVariables(): void {
    this.uploadDialogService
      .showDialog(VariablesUploadFormComponent, {
        header: this.translateService.instant('workflow.variables.import.dialog.header'),
        width: '50%',
      })
      .subscribe((request: { variables: VariableImportItem[]; override: boolean }) => {
        if (!request) return;

        this.handleImportResponse(request);
      });
  }

  private handleImportResponse(request: { variables: VariableImportItem[]; override: boolean }): void {
    runInInjectionContext(this.injector, () => {
      const resultsEffect = effect(() => {
        const results = this.variablesStore.lastImportResult();
        if (results) {
          this.showImportResult(results);
          this.variablesStore.clearImportResult();
          resultsEffect.destroy();
        }
      });
    });

    this.variablesStore.importVariables({ variables: request.variables, overwrite: request.override });
  }

  private showImportResult(results: VariableImportResponse[]): void {
    this.uploadDialogService.showDialog(VariablesImportResultComponent, {
      header: this.translateService.instant('workflow.variables.tree.import.results.header'),
      width: '70%',
      height: '80%',
      data: results,
    });
  }
}
